import { useQuery } from '@tanstack/react-query'
import {
	ArrowLeftIcon,
	Button,
	Input,
	Label,
	ModuleHeader,
	SearchIcon,
	SpinnerWithBackground,
	Text,
} from '@viapeople/components'
import { useState } from 'react'
import { useNavigate, useParams } from 'react-router-dom'
import { type Column, GenericTable } from '../../components/GenericTable'
import { sessionStore } from '../../sharedStores/sessionStore'
import { trpc } from '../../utils/trpc'

interface IndividualResponse {
	participantId: string
	participantFirstName?: string
	participantLastName?: string
	participantEmail?: string
	textResponse: string | null
	ratingResponse: number | null
	status: 'pending' | 'completed'
	submittedAt: Date | null
}

export const LoopResultsDetailPage = () => {
	const { loopId, executionId } = useParams<{
		loopId: string
		executionId: string
	}>()
	const { user } = sessionStore()
	const organizationId = user?.organizationId || ''
	const navigate = useNavigate()
	const [searchQuery, setSearchQuery] = useState('')

	// Fetch execution results with participant details
	const {
		data: executionResults,
		isLoading,
		isError,
	} = useQuery({
		...trpc.client.loops.getExecutionResultsWithParticipants.queryOptions({
			executionId: executionId || '',
			organizationId: organizationId || '',
		}),
		enabled: !!executionId && !!organizationId,
	})

	// Fetch loop info for context
	const { data: loopData } = useQuery({
		...trpc.client.loops.getById.queryOptions({
			id: loopId || '',
			organizationId: organizationId || '',
		}),
		enabled: !!loopId && !!organizationId,
	})

	const handleBack = () => {
		navigate(`/loops/${loopId}/results`)
	}

	// Filter responses based on search query
	const filteredResponses =
		executionResults?.individualResponses?.filter((response) => {
			if (!searchQuery) return true
			const query = searchQuery.toLowerCase()
			return (
				response.participantFirstName?.toLowerCase().includes(query) ||
				response.participantLastName?.toLowerCase().includes(query) ||
				response.participantEmail?.toLowerCase().includes(query) ||
				response.textResponse?.toLowerCase().includes(query)
			)
		}) || []

	const columns: Column<IndividualResponse>[] = [
		{
			field: 'participantFirstName',
			label: 'Self Evaluator',
			colSpan: 2,
			sortable: true,
			render: (response: IndividualResponse) => (
				<div>
					<Text variant='small' weight='semibold'>
						{response.participantFirstName} {response.participantLastName}
					</Text>
					<Text variant='xsmall' className='text-gray-600'>
						{response.participantEmail}
					</Text>
				</div>
			),
		},
		{
			field: 'status',
			label: 'Status',
			colSpan: 1,
			sortable: true,
			render: (response: IndividualResponse) => (
				<Label color={response.status === 'completed' ? 'green' : 'default'} className='w-25!'>
					{response.status === 'completed' ? 'Completed' : 'Pending'}
				</Label>
			),
		},
		{
			field: 'ratingResponse',
			label: 'Rating',
			colSpan: 1,
			sortable: true,
			render: (response: IndividualResponse) => (
				<Text variant='small'>
					{response.ratingResponse !== null ? `${response.ratingResponse} / ${loopData?.ratingScale || 5}` : '-'}
				</Text>
			),
		},
		{
			field: 'textResponse',
			label: 'Text Response',
			colSpan: 3,
			render: (response: IndividualResponse) => (
				<Text variant='small' className='line-clamp-2'>
					{response.textResponse || '-'}
				</Text>
			),
		},
		{
			field: 'submittedAt',
			label: 'Submitted',
			colSpan: 1,
			sortable: true,
			render: (response: IndividualResponse) => (
				<Text variant='xsmall'>
					{response.submittedAt
						? new Date(response.submittedAt).toLocaleDateString('en-US', {
								month: 'short',
								day: 'numeric',
								year: 'numeric',
							})
						: '-'}
				</Text>
			),
		},
	]

	if (isLoading) {
		return (
			<main className='w-full p-6'>
				<SpinnerWithBackground backgroundClass='opacity-100' />
			</main>
		)
	}

	if (isError || !executionResults) {
		return (
			<main className='w-full p-6'>
				<ModuleHeader
					breadcrumb={[
						{ label: 'Loops', to: '/loops' },
						{
							label: loopData?.name || 'Loop',
							to: `/loops/${loopId}`,
						},
						{ label: 'Results', to: `/loops/${loopId}/results` },
						{ label: 'Details', to: '#' },
					]}
					title='Execution Details'
				/>
				<div className='mt-6 text-center'>
					<Text variant='medium' weight='semibold' className='mb-2'>
						Unable to load execution details
					</Text>
					<Text variant='small' className='text-gray-600'>
						There was a problem loading the details for this execution. Please try again later.
					</Text>
					<Button variant='secondary' onClick={handleBack} className='mt-4'>
						<ArrowLeftIcon className='w-4 h-4 mr-2' />
						Back to Results
					</Button>
				</div>
			</main>
		)
	}

	const iterationNumber = 1 // TODO: Calculate from execution history when available

	return (
		<main className='w-full p-6'>
			<ModuleHeader
				breadcrumb={[
					{ label: 'Loops', to: '/loops' },
					{
						label: loopData?.name || executionResults.loopName,
						to: `/loops/${loopId}`,
					},
					{ label: 'Results', to: `/loops/${loopId}/results` },
					{ label: `Iteration #${iterationNumber}`, to: '#' },
				]}
				title={`${executionResults.loopName} - Iteration #${iterationNumber} Details`}
			/>

			<Button variant='secondary' size='small' onClick={handleBack} className='mt-4'>
				<ArrowLeftIcon className='w-4 h-4 mr-2' />
				Back to Results
			</Button>

			{/* Summary Statistics */}
			<div className='mt-6 bg-gray-50 p-6 rounded-lg'>
				<div className='grid grid-cols-2 md:grid-cols-4 gap-6'>
					<div>
						<Text variant='xsmall' className='text-gray-600'>
							Launched
						</Text>
						<Text variant='small' weight='semibold'>
							{new Date(executionResults.launchedAt).toLocaleDateString('en-US', {
								month: 'long',
								day: 'numeric',
								year: 'numeric',
							})}
						</Text>
					</div>
					<div>
						<Text variant='xsmall' className='text-gray-600'>
							Status
						</Text>
						<Label color={executionResults.status === 'active' ? 'green' : 'default'} className='w-20!'>
							{executionResults.status === 'active' ? 'Active' : 'Closed'}
						</Label>
					</div>
					<div>
						<Text variant='xsmall' className='text-gray-600'>
							Response Rate
						</Text>
						<Text variant='small' weight='semibold'>
							{executionResults.statistics.responseRate}%
						</Text>
						<Text variant='xsmall' className='text-gray-600'>
							{executionResults.statistics.completedResponses} / {executionResults.statistics.totalParticipants}{' '}
							participants
						</Text>
					</div>
					{executionResults.statistics.averageRating !== null && (
						<div>
							<Text variant='xsmall' className='text-gray-600'>
								Average Rating
							</Text>
							<Text variant='small' weight='semibold'>
								{executionResults.statistics.averageRating} / {loopData?.ratingScale || 5}
							</Text>
						</div>
					)}
				</div>
			</div>

			{/* Search and Filters */}
			<div className='mt-6 flex items-center gap-4'>
				<div className='relative flex-1'>
					<SearchIcon className='absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-400' />
					<Input
						type='text'
						placeholder='Search by name, email, or response...'
						value={searchQuery}
						onChange={(e) => setSearchQuery(e.target.value)}
						className='pl-10'
					/>
				</div>
				<Text variant='small' className='text-gray-600'>
					{filteredResponses.length} of {executionResults.individualResponses?.length || 0} responses
				</Text>
			</div>

			{/* Participant Responses Table */}
			<div className='mt-6'>
				{filteredResponses.length > 0 ? (
					<GenericTable<IndividualResponse, never>
						data={filteredResponses}
						columns={columns}
						idField='participantId'
						showPagination={true}
						queryOptionsGenerator={() => ({ enabled: false })}
						onRowClick={(response: IndividualResponse) => {
							// Could navigate to participant detail view if needed
							console.log('Selected response:', response)
						}}
					/>
				) : (
					<div className='border border-neutral-200 rounded-lg p-12 text-center'>
						<Text variant='medium' weight='semibold' className='mb-2'>
							{searchQuery ? 'No matching responses found' : 'No responses yet'}
						</Text>
						<Text variant='small' className='text-neutral-500'>
							{searchQuery
								? 'Try adjusting your search criteria'
								: 'Responses will appear here once participants submit them'}
						</Text>
					</div>
				)}
			</div>

			{/* Question Context */}
			<div className='mt-8 border border-neutral-200 rounded-lg p-6'>
				<Text variant='small' weight='semibold' className='mb-4'>
					Question Context
				</Text>
				<div className='space-y-4'>
					{loopData?.textQuestionEnabled && loopData?.textQuestionLabel && (
						<div>
							<Text variant='xsmall' className='text-gray-600'>
								Text Question
							</Text>
							<Text variant='small'>{loopData.textQuestionLabel}</Text>
						</div>
					)}
					{loopData?.ratingQuestionEnabled && loopData?.ratingQuestionLabel && (
						<div>
							<Text variant='xsmall' className='text-gray-600'>
								Rating Question
							</Text>
							<Text variant='small'>
								{loopData.ratingQuestionLabel} (Scale: 1-{loopData.ratingScale || 5})
							</Text>
						</div>
					)}
				</div>
			</div>
		</main>
	)
}
